// Eskom PARCC — tiny read-after-write poller.
//
// Firebase Data Connect reads can briefly miss a row that was just written
// (read-after-write visibility lag). When a UNIQUE-constraint violation has
// already PROVEN a row exists, the immediate re-read can still return empty — so
// we poll a few times with a short linear backoff until it appears. Pure: the
// `sleep` is injectable so the backoff is instant under test.
//
// (Introduced for Iris's working-paper save: a freshly-inserted working paper
// could race the recovery re-read in upsertWorkingPaperForTx and hard-fail with
// ALREADY_EXISTS — see src/lib/dbWrite.ts.)

/**
 * Poll `read` up to `attempts` times, returning its first truthy result. Sleeps
 * (linear backoff: 120ms, 240ms, …) BETWEEN attempts only — never before the
 * first read, nor after the last. Returns `undefined` if the value never appears
 * within the budget, so the caller can surface the original error.
 */
export async function retryUntilFound<T>(
  read: () => Promise<T | undefined | null>,
  attempts = 5,
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
): Promise<T | undefined> {
  for (let i = 0; i < attempts; i++) {
    const got = await read();
    if (got) return got;
    if (i < attempts - 1) await sleep(120 * (i + 1));
  }
  return undefined;
}
